// CreditNova Override Audit Display Component
// Shows the audit trail of all overrides applied to a report

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, RotateCcw, FileEdit } from 'lucide-react';

interface OverrideEntry {
  id: string;
  fieldPath: string;
  fieldLabel: string;
  originalValue: string;
  overrideValue: string;
  justification: string;
  createdAt: string;
  userId?: string;
}

interface OverrideAuditDisplayProps {
  overrides: OverrideEntry[];
  title?: string;
}

export function OverrideAuditDisplay({ overrides, title = 'Override Audit Trail' }: OverrideAuditDisplayProps) {
  if (!overrides || overrides.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileEdit className="w-10 h-10 text-muted-foreground/40 mb-3" />
        <p className="text-muted-foreground text-sm">
          No overrides have been applied to this report.
        </p>
      </div>
    );
  }

  // Group by field path
  const groupedOverrides = overrides.reduce((acc, o) => {
    if (!acc[o.fieldPath]) acc[o.fieldPath] = [];
    acc[o.fieldPath].push(o);
    return acc;
  }, {} as Record<string, OverrideEntry[]>);

  const fieldCount = Object.keys(groupedOverrides).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <Badge variant="secondary" className="bg-accent-500/10 text-accent-500 border-accent-500/20">
          {overrides.length} override{overrides.length !== 1 ? 's' : ''} across {fieldCount} field{fieldCount !== 1 ? 's' : ''}
        </Badge>
      </div>

      <div className="space-y-3">
        {Object.entries(groupedOverrides).map(([fieldPath, entries]) => {
          return (
            <div
              key={fieldPath}
              className="rounded-lg border border-border overflow-hidden bg-card"
            >
              {/* Field header */}
              <div className="px-4 py-3 bg-muted/50 border-b border-border flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">
                  {entries[0].fieldLabel || fieldPath}
                </span>
                <span className="text-xs text-muted-foreground font-mono">
                  {fieldPath}
                </span>
              </div>

              {/* Entries */}
              {entries.map((entry, idx) => {
                const entryReverted = entry.originalValue === entry.overrideValue;

                return (
                  <div
                    key={entry.id || idx}
                    className={cn(
                      'px-4 py-3',
                      idx > 0 && 'border-t border-border',
                    )}
                  >
                    {/* Timestamp + revert badge */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-muted-foreground">
                        {new Date(entry.createdAt).toLocaleString()}
                      </span>
                      {entryReverted && (
                        <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400 gap-1">
                          <RotateCcw className="w-3 h-3" />
                          Reverted
                        </Badge>
                      )}
                    </div>

                    {/* Value change */}
                    <div className="flex items-center gap-2 text-sm">
                      <span className="line-through text-muted-foreground/70 font-mono">
                        {entry.originalValue}
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/50 flex-shrink-0" />
                      <span className={cn(
                        'font-medium font-mono',
                        entryReverted ? 'text-muted-foreground' : 'text-accent-400'
                      )}>
                        {entry.overrideValue}
                      </span>
                    </div>

                    {/* Justification */}
                    {entry.justification && (
                      <p className="text-xs text-muted-foreground mt-2 italic">
                        &ldquo;{entry.justification}&rdquo;
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default OverrideAuditDisplay;
